<template>
  <ModalDialog header-text="Send Email" @close-modal="closeModal" open>
    <template v-slot:content>
      <form class="email-form" @submit.prevent="onSubmitAsync">
        <p>
          Separate email addresses with semi-colons [no spaces allowed]<br />
          Example: RabbiSmith@ou.org;RabbiCohen@ou.org
        </p>
        <div class="inline-form-control">
          <label for="email-recipient">To:</label>
          <input
            type="text"
            id="email-recipient"
            name="email-recipient"
            v-model="emailRecipient"
          />
        </div>
        <div class="inline-form-control">
          <label for="emailCc">Cc:</label>
          <input type="text" id="emailCc" name="emailCc" v-model="emailCc" />
        </div>
        <div class="inline-form-control">
          <label for="emailBcc">Bcc:</label>
          <input type="text" id="emailBcc" name="emailBcc" v-model="emailBcc" />
        </div>
        <div class="inline-form-control">
          <label for="subject">Subject:</label>
          <input type="text" id="subject" name="subject" v-model="subject" />
        </div>
        <div class="form-control">
          <textarea
            id="message"
            class="message-textarea"
            name="message"
            v-model="message"
            :rows="10"
            placeholder="Enter your message here..."
          ></textarea>
        </div>
        <Button
          type="submit"
          class="submit-button"
          theme-color="primary"
          :disabled="message.length == 0 || formSubmitting"
          data-cy="submit-button"
          >Send</Button
        >
      </form>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { useMainStore } from "@/stores/useMainStore";
import { onBeforeMount, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { PersonJobTb } from "@/Domain/PersonJobTb";
import type { ChometzCompany } from "@/Domain/ChometzCompany";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  company: ChometzCompany;
  chometzSaleSeason?: number;
}>();

const emit = defineEmits(["closeModal"]);

const subject = ref<string>("");
const emailRecipient = ref<string>("");
const message = ref<string>("");
const emailCc = ref<string>("");
const emailBcc = ref<string>("");
const formSubmitting = ref<boolean>(false);
const personJob = ref<PersonJobTb | null>(null);

const getCurrentPersonJobAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetCurrentPersonJob", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (response) => {
      personJob.value = response;
    },
    "WorkflowEmailModal.vue",
    "getCurrentPersonJobAsync"
  );
};

const setDefaultValues = function () {
  if (!personJob.value) return;

  const fromEmailAddress = personJob.value.personTb.Contacts[0].Businessemail;
  const fromEmailName =
    personJob.value.personTb.FirstNameLastNameTitleIfRabbiOnly.trim();
  const fromEmailPhone = personJob.value.personTb.Contacts[0].Businessphone;
  const ResendEmail = props.company.EmailSent;

  subject.value =
    `Chometz Sale - ${
      !props.company.IsPlant ? "Company" : "Plant"
    } - Passover Season ` +
    (props.chometzSaleSeason ?? new Date().getFullYear().toString());

  emailRecipient.value = props.company.ContactEmail;

  message.value = ResendEmail
    ? //  Resend email default text
      `  
    Company Name: ${props.company.CompanyName}
    ${props.company.IsPlant ? "Plant Name: " + props.company.PlantName : ""}
    Account Administrator: ${props.company.RcFormattedName}
    Chometz Sale Administrator: Rabbi Nachum Rabinowitz\n
    Hi,\n
    Pursuant to my earlier email(s), I am asking you to authorize the sale of your company’s Chometz for the duration of the Passover holiday. As a Jewish-owned company, failure to arrange an appropriate transfer of certain grain-based products can potentially jeopardize kosher approval for those Chometz materials and products in which they are used.
    \n
    Please click on one of the links below to authorize the sale of Chometz for Passover ${
      props.chometzSaleSeason ?? new Date().getFullYear()
    }.
    \n
    If you have any questions, please contact me.\n

    Thank you.
    ${fromEmailName}
    ${fromEmailAddress}
    ${fromEmailPhone}`.replace(/(\n\s+)|(\n\n\s+)/g, (match) => {
        return match.startsWith("\n\n") ? "\n\n" : "\n";
      })
    : //  Original email default text
      `
    Company Name: ${props.company.CompanyName}
    ${props.company.IsPlant ? "Plant Name: " + props.company.PlantName : ""}
    Account Administrator: ${props.company.RcFormattedName}
    Chometz Sale Administrator: Rabbi Nachum Rabinowitz\n
    Hi,\n
    Please be advised that kosher law forbids Jewish ownership of certain grain-based products (*Chometz) for the duration of the Passover holiday. Furthermore, Chometz substances that were Jewish owned during Passover are not considered kosher.
    \n
    Therefore, to maintain your company’s kosher program in good-standing, as an OU Kosher certified Jewish owned business it is necessary that a sale of Chometz materials to a non-Jew take place for the duration of the Passover holiday.
    \n
    
    Please click on one of the links below to authorize the sale of Chometz for Passover ${
      props.chometzSaleSeason ?? new Date().getFullYear()
    }.\n
    
    If you have any questions, please contact me.\n

    Thank you.
    ${fromEmailName}
    ${fromEmailAddress}
    ${fromEmailPhone}`.replace(/(\n\s+)|(\n\n\s+)/g, (match) => {
        return match.startsWith("\n\n") ? "\n\n" : "\n";
      });
};

const onSubmitAsync = async function () {
  if (formSubmitting.value) return;

  formSubmitting.value = true;

  const sendEmailPromise = api.post("SendChometzCompanyEmail", {
    UserLogin: main.userLogin,
    CompanyId: !props.company.IsPlant ? props.company.CompanyId : 0,
    PlantId: props.company.IsPlant ? props.company.PlantId : 0,
    Message: message.value,
    Subject: subject.value,
    SendTo: emailRecipient.value,
    Cc: emailCc.value,
    Bcc: emailBcc.value,
  });

  const errorNotification = () => {
    notificationStore.notify({
      message: `Error sending email. Please try again.`,
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
  };

  await handleResponsePromiseAsync(
    sendEmailPromise,
    (data) => {
      if (!data) {
        errorNotification();
        return;
      }
      const company = props.company;
      company.EmailSent = true;
      company.EmailSentDate = new Date();

      notificationStore.notify({
        message: "Email sent successfully",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
      closeModal();
    },
    "ChometzEmailModal.vue",
    "onSubmit",
    () => {
      errorNotification();
    }
  );

  formSubmitting.value = false;
};

const closeModal = function () {
  emit("closeModal");
};

onBeforeMount(async () => {
  await getCurrentPersonJobAsync();
  setDefaultValues();
});
</script>

<style lang="scss">
.email-form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  gap: 16px;

  @include media-breakpoint-up(sm) {
    width: 500px;
  }

  @include media-breakpoint-up(lg) {
    width: 800px;
  }

  .message-textarea {
    resize: vertical;
  }

  .attachment-link {
    color: #337ab7;
    cursor: pointer;

    &:hover {
      color: #0a58ca;
    }
  }

  .submit-button {
    width: 200px;
    margin: 0 auto;
  }

  .inline-form-control {
    display: flex;
    margin-bottom: 1rem;
    align-items: center;

    label {
      height: 36px;
      line-height: 24px;
      font-size: 1rem;
      background: #f7f7f7;
      padding: 5px 10px;
      border-radius: 4px 0 0 4px;
      border: 1px solid #ddd;
      padding-right: 24px;
      flex-basis: 25%;
    }

    input {
      height: 36px;
      padding: 0.5rem;
      border: 1px solid #ccc;
      font-size: 1rem;
      border-radius: 0 4px 4px 0;
      width: 100%;
      flex-basis: 75%;
    }
  }

  .k-svg-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
